/**
 * UnitSelector — 4-filter cascading unit picker (Rua → Número → Quadra → Lote)
 * for street condos, or 2-filter (Quadra/Bloco → Lote/Unidade) for block condos.
 *
 * Drop-in replacement for an AsyncSelect unit search wherever a single unit
 * needs to be selected.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Select, { SingleValue } from 'react-select';
import { condoApi } from '../services/api';
import { getBlockLabel, getUnitLabel, CondoType } from '../utils/terminology';

interface FlatUnit {
  id: string;
  number: string;
  block_name: string;
  block_id: string;
  street_name?: string;
  street_id?: string;
  street_number?: string;
}

export interface UnitOption {
  value: string;       // unit id
  label: string;       // display string
  unitNumber?: string;
  blockName?: string;
  streetName?: string;
  streetNumber?: string;
}

interface UnitSelectorProps {
  customerId: string;
  value: UnitOption | null;
  onChange: (option: UnitOption | null) => void;
  condoType?: CondoType;
  darkMode?: boolean;
  disabled?: boolean;
}

type Opt = SingleValue<{ value: string; label: string }>;

const clsNames = (darkMode: boolean) => ({
  control: (s: any) =>
    `!shadow-sm !rounded-lg !min-h-[36px] ${darkMode
      ? `!bg-gray-800 !border-gray-600 ${s.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
      : `!border-gray-300 ${s.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`}`,
  input: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
  option: (s: any) =>
    `!text-sm ${darkMode
      ? s.isSelected ? '!bg-primary-600 !text-white' : s.isFocused ? '!bg-gray-700 !text-white' : '!bg-gray-800 !text-gray-200'
      : s.isSelected ? '!bg-primary-600 !text-white' : s.isFocused ? '!bg-gray-100 !text-gray-900' : '!text-gray-900'}`,
  placeholder: () => `!text-sm ${darkMode ? '!text-gray-500' : '!text-gray-400'}`,
  singleValue: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
  menu: () => darkMode ? '!bg-gray-800 !border-gray-600' : '!bg-white',
  menuList: () => darkMode ? '!bg-gray-800' : '!bg-white',
});

/**
 * Render the dropdown into <body> instead of next to the control.
 *
 * A high z-index does nothing against an ancestor with `overflow-hidden` — the menu is simply
 * clipped at the container's edge, showing a couple of options and cutting the rest off. This
 * selector is used inside cards and modals that clip (the roles card in PermissionsTab is
 * `rounded-lg border overflow-hidden`), so the menu has to leave the clipping context entirely.
 *
 * menuPosition fixed keeps it aligned to the control once it lives on <body>.
 */
const menuPortalProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
  menuPosition: 'fixed' as const,
};

const stls = (darkMode: boolean) => ({
  control: (b: any) => ({ ...b, minHeight: '36px', backgroundColor: darkMode ? '#1f2937' : b.backgroundColor }),
  menu: (b: any) => ({ ...b, zIndex: 9999, backgroundColor: darkMode ? '#1f2937' : b.backgroundColor }),
  // The portalled menu is a sibling of the app root, so it needs its own stacking order.
  menuPortal: (b: any) => ({ ...b, zIndex: 9999 }),
  option: (b: any, s: any) => ({
    ...b,
    backgroundColor: darkMode
      ? s.isSelected ? '#7c3aed' : s.isFocused ? '#374151' : '#1f2937'
      : s.isSelected ? '#7c3aed' : s.isFocused ? '#f3f4f6' : '#ffffff',
    color: darkMode ? '#e5e7eb' : s.isSelected ? '#ffffff' : '#111827',
  }),
});

function buildLabel(u: FlatUnit): string {
  const parts: string[] = [];
  if (u.block_name) parts.push(u.block_name);
  if (u.street_name) parts.push(u.street_number ? `${u.street_name}, ${u.street_number}` : u.street_name);
  parts.push(u.number);
  return parts.join(' – ');
}

export const UnitSelector: React.FC<UnitSelectorProps> = ({
  customerId,
  value,
  onChange,
  condoType,
  darkMode = false,
  disabled = false,
}) => {
  const [allUnits, setAllUnits] = useState<FlatUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasStreets, setHasStreets] = useState(false);

  // Filters
  const [selectedStreet, setSelectedStreet] = useState<Opt>(null);
  const [streetNumber, setStreetNumber] = useState('');
  const [blockFilter, setBlockFilter] = useState<Opt>(null);

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    condoApi.searchUnits(customerId, '', { limit: 5000 })
      .then((res: any) => {
        const data: FlatUnit[] = res.data?.data || [];
        setAllUnits(data);
        setHasStreets(data.some(u => u.street_name));
      })
      .catch(() => setAllUnits([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  // ---- Street-based derivations ----
  const streetOptions = useMemo(() => {
    const seen = new Map<string, string>();
    allUnits.forEach(u => { if (u.street_id && u.street_name) seen.set(u.street_id, u.street_name); });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { numeric: true }));
  }, [allUnits]);

  const streetFiltered = useMemo(() =>
    selectedStreet ? allUnits.filter(u => u.street_id === selectedStreet.value) : allUnits,
    [allUnits, selectedStreet]);

  const numberOptions = useMemo(() => {
    const seen = new Set<string>();
    streetFiltered.forEach(u => { if (u.street_number) seen.add(u.street_number); });
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
      .map(n => ({ value: n, label: n }));
  }, [streetFiltered]);

  const numberFiltered = useMemo(() =>
    streetNumber ? streetFiltered.filter(u => (u.street_number || '') === streetNumber) : streetFiltered,
    [streetFiltered, streetNumber]);

  const blockFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    numberFiltered.forEach(u => { if (u.block_id) seen.set(u.block_id, u.block_name); });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { numeric: true }));
  }, [numberFiltered]);

  const loteOptions = useMemo(() => {
    let list = numberFiltered;
    if (blockFilter) list = list.filter(u => u.block_id === blockFilter.value);
    return list
      .sort((a, b) => a.number.localeCompare(b.number, 'pt-BR', { numeric: true }))
      .map(u => ({ value: u.id, label: u.number }));
  }, [numberFiltered, blockFilter]);

  const loteValue = useMemo(() => {
    if (!value) return null;
    const u = allUnits.find(x => x.id === value.value);
    return u ? { value: u.id, label: u.number } : null;
  }, [value, allUnits]);

  const handleLote = useCallback((opt: Opt) => {
    if (!opt) { onChange(null); return; }
    const u = allUnits.find(x => x.id === opt.value);
    if (!u) { onChange(null); return; }
    onChange({ value: u.id, label: buildLabel(u), unitNumber: u.number, blockName: u.block_name, streetName: u.street_name, streetNumber: u.street_number });
    // sync filters
    if (u.street_id) setSelectedStreet({ value: u.street_id, label: u.street_name || '' });
    setStreetNumber(u.street_number || '');
    setBlockFilter({ value: u.block_id, label: u.block_name });
  }, [allUnits, onChange]);

  const handleStreet = useCallback((opt: Opt) => {
    setSelectedStreet(opt);
    setStreetNumber('');
    setBlockFilter(null);
    onChange(null);
  }, [onChange]);

  const handleNumber = useCallback((opt: Opt) => {
    const num = opt?.value || '';
    setStreetNumber(num);
    setBlockFilter(null);
    onChange(null);
    if (num && selectedStreet) {
      const match = allUnits.find(u => u.street_id === selectedStreet.value && (u.street_number || '') === num);
      if (match) {
        onChange({ value: match.id, label: buildLabel(match) });
        setBlockFilter({ value: match.block_id, label: match.block_name });
      }
    }
  }, [allUnits, selectedStreet, onChange]);

  // ---- Block-based derivations (no streets) ----
  const blockOptions = useMemo(() => {
    const seen = new Map<string, string>();
    allUnits.forEach(u => { if (u.block_id) seen.set(u.block_id, u.block_name); });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { numeric: true }));
  }, [allUnits]);

  const unitOptions = useMemo(() => {
    let list = blockFilter ? allUnits.filter(u => u.block_id === blockFilter.value) : [];
    return list
      .sort((a, b) => a.number.localeCompare(b.number, 'pt-BR', { numeric: true }))
      .map(u => ({ value: u.id, label: u.number }));
  }, [allUnits, blockFilter]);

  const unitValue = useMemo(() => {
    if (!value) return null;
    const u = allUnits.find(x => x.id === value.value);
    return u ? { value: u.id, label: u.number } : null;
  }, [value, allUnits]);

  const handleBlock = useCallback((opt: Opt) => {
    setBlockFilter(opt);
    onChange(null);
  }, [onChange]);

  const handleUnit = useCallback((opt: Opt) => {
    if (!opt) { onChange(null); return; }
    const u = allUnits.find(x => x.id === opt.value);
    if (!u) { onChange(null); return; }
    onChange({ value: u.id, label: buildLabel(u), unitNumber: u.number, blockName: u.block_name, streetName: u.street_name, streetNumber: u.street_number });
  }, [allUnits, onChange]);

  if (loading) {
    return <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Carregando...</p>;
  }

  const blockLabel = getBlockLabel(condoType);
  const unitLabel = getUnitLabel(condoType);

  if (hasStreets) {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Rua <span className="opacity-60">(Filtro)</span>
            </label>
            <Select
              options={streetOptions}
              value={selectedStreet}
              onChange={handleStreet}
              placeholder="Selecione..."
              isClearable isSearchable
              isDisabled={disabled}
              classNames={clsNames(darkMode)}
              styles={stls(darkMode)}
              {...menuPortalProps}
              noOptionsMessage={() => 'Nenhuma rua'}
            />
          </div>
          <div>
            <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Número <span className="opacity-60">(Filtro)</span>
            </label>
            <Select
              options={numberOptions}
              value={streetNumber ? { value: streetNumber, label: streetNumber } : null}
              onChange={handleNumber}
              placeholder="Selecione..."
              isClearable isSearchable
              isDisabled={disabled}
              classNames={clsNames(darkMode)}
              styles={stls(darkMode)}
              {...menuPortalProps}
              noOptionsMessage={() => selectedStreet ? 'Nenhum número' : 'Selecione a rua primeiro'}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Quadra <span className="opacity-60">(Filtro)</span>
            </label>
            <Select
              options={blockFilterOptions}
              value={blockFilter}
              onChange={(opt) => { setBlockFilter(opt); onChange(null); }}
              placeholder="Selecione..."
              isClearable isSearchable
              isDisabled={disabled}
              classNames={clsNames(darkMode)}
              styles={stls(darkMode)}
              {...menuPortalProps}
              noOptionsMessage={() => 'Nenhuma quadra'}
            />
          </div>
          <div>
            <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Lote
            </label>
            <Select
              options={loteOptions}
              value={loteValue}
              onChange={handleLote}
              placeholder="Buscar lote..."
              isClearable isSearchable
              isDisabled={disabled}
              classNames={clsNames(darkMode)}
              styles={stls(darkMode)}
              {...menuPortalProps}
              noOptionsMessage={() => 'Nenhum lote'}
            />
          </div>
        </div>
      </div>
    );
  }

  // Block condo: 2 filters
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {blockLabel} <span className="opacity-60">(Filtro)</span>
        </label>
        <Select
          options={blockOptions}
          value={blockFilter}
          onChange={handleBlock}
          placeholder="Selecione..."
          isClearable isSearchable
          isDisabled={disabled}
          classNames={clsNames(darkMode)}
          styles={stls(darkMode)}
              {...menuPortalProps}
          noOptionsMessage={() => `Nenhum ${blockLabel.toLowerCase()}`}
        />
      </div>
      <div>
        <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {unitLabel}
        </label>
        <Select
          options={unitOptions}
          value={unitValue}
          onChange={handleUnit}
          placeholder={`Buscar ${unitLabel.toLowerCase()}...`}
          isClearable isSearchable
          isDisabled={disabled || !blockFilter}
          classNames={clsNames(darkMode)}
          styles={stls(darkMode)}
              {...menuPortalProps}
          noOptionsMessage={() => blockFilter ? `Nenhum ${unitLabel.toLowerCase()}` : `Selecione ${blockLabel.toLowerCase()} primeiro`}
        />
      </div>
    </div>
  );
};

export default UnitSelector;
